import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useModal } from "react-modal-hook";

import bankAccountApi, { useBankAccountById } from "../../../api/bankAccount";
import { Loader, toast } from "../../../common/components/core";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../../common/hooks/useGeneralErrorHandler";
import { GenericError, composeErrorHandlers } from "../../../utils/errorHandling";
import { BankStatus } from "../../../types/common";



type ReviewApproveHandler = {
    isSubmitting: boolean;
    handler: (
        accountType: string,
        accountTypeLow: string,
        status: BankStatus | undefined,
    ) => void;
};

const useReviewApproveHandler = (accountId: string): ReviewApproveHandler => {
    const [ isSubmitting, setSubmitting ] = useState(false);
    const [ accountType, setAccountType ] = useState<string>("");
    const [ accountTypeLow, setAccountTypeLow ] = useState<string>("");
    const [ status, setStatus ] = useState<BankStatus>();
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const { mutate } = useBankAccountById(accountId);
    const typeText = status === BankStatus.PENDING_APPROVAL ? "Set up" : "Edit";
    const typeTextLow = status === BankStatus.PENDING_APPROVAL ? "set up" : "edit";

    const [show, hide] = useModal(() => {
        const confirm = async () => {
            setSubmitting(true);
            try {
                const res = await bankAccountApi.review(accountId, 0, null);
                mutate(res);
                toast(`${accountType} account ${typeTextLow} approved`);
            } catch (e) {
                composeErrorHandlers(
                    onAuthError,
                    useGeneralError,
                )(e as GenericError);
            }
            hide();
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Loader overlay isLoading={isSubmitting}>
                    <Modal.Header>
                        <h2 className="mb-0">
                            {`Approve ${accountType} Account ${typeText}`}
                        </h2>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{`Are you sure you want to approve this ${accountTypeLow} account ${typeTextLow}?`}</p>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-between px-3">
                        <Button variant="light.tertiary" onClick={hide}>
                            Cancel
                        </Button>
                        <Button variant="light.primary" onClick={confirm}>
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Loader>
            </Modal>
            </ThemeProviderV2>
        );
    }, [accountId, mutate, isSubmitting, accountType, accountTypeLow, status, onAuthError]);

    return {
        isSubmitting,
        handler: (accountType, accountTypeLow, status) => {
            setAccountType(accountType);
            setAccountTypeLow(accountTypeLow);
            setStatus(status);
            show();
        },
    };
};

export default useReviewApproveHandler;
